import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Col, Card } from 'react-bootstrap';
import { useAxios } from '../../hooks/useAxios';
import Alert from 'react-bootstrap/Alert';
import { WorkflowConfig } from '../workflow-config';
import { Preloader } from '../preloader';
import WFSettings from '../../data/platform/workflow_settings.json';

const ArcosWorkflow = ({
  combination_id,
  connectors,
  activeTab,
  recipeActive,
  recipeSetupStatus,
  connected,
  setRecipeDisabled,
}) => {
  const [settingUpRecipe, setSettingUpRecipe] = useState(true);

  const [tag, setTag] = useState('');
  const [url, setUrl] = useState('');
  const [doing, setDoing] = useState('getURLs');
  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState('');
  const [currentlyCrawling, setCurrentlyCrawling] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState('');

  const { data: urlRemovedResp = null, fetchNow: removeURLToKB } = useAxios();
  const { data: urlAddedResp = null, fetchNow: addURLToKB } = useAxios();
  const { data: urlList = {}, fetchNow: getURLs, resetData: resetURLList } = useAxios();
  const { data: urlCrawlStatus = null, fetchNow: crawlAllUrls } = useAxios();

  const [jobStatusIntervalId, setJobStatusIntervalId] = useState();
  const [configRequired, setConfigRequired] = useState(false);
  const [escalationContactEmail, setEscalationContactEmail] = useState('');

  const {
    get_config,
    wf_config,
    save_wf_config,
    fetchSetting,
    setFetchSetting,
    configAlertType,
    configAlertContent,
    wf_configSet,
  } = WorkflowConfig({
    combination_id: combination_id,
  });

  useEffect(() => {
    setSettingUpRecipe(recipeSetupStatus === 'complete' ? false : true);
    if (WFSettings.requiresConfigBeforeStart.includes(combination_id)) {
      setConfigRequired(true);
    }

    if (configRequired && !wf_configSet) {
      setRecipeDisabled(true);
    } else {
      setRecipeDisabled(false);
    }

    if (wf_config && fetchSetting === 'get_config') {
      try {
        setEscalationContactEmail(wf_config.variables.arcos.emails['ESCALATIONCONTACT']);
      } catch (error) {}

      setFetchSetting('');
    }
  }, [recipeSetupStatus, configRequired, wf_config, wf_configSet, fetchSetting]);

  useEffect(() => {
    if (!wf_configSet) {
      get_config();
    }
    get_URLS();
  }, []);

  useEffect(() => {
    if (urlList) {
      setLoading(false);

      setCrawlStatus(urlList['crawler_status']);

      if (crawlStatus === 'CRAWLING' || crawlStatus === 'NEW') {
        refresh_url_list();
        setCurrentlyCrawling(true);
      } else {
        setCurrentlyCrawling(false);
        stopPolling();
      }
    }

    if (doing === 'crawlURLs' && urlCrawlStatus !== null) {
      setCrawlStatus(urlCrawlStatus['crawler_status']);
      refresh_url_list();
      setDoing('getURLs');
    }
  }, [doing, urlCrawlStatus, urlList, crawlStatus]);

  useEffect(() => {
    if (doing === 'saveURLs' && urlAddedResp?.id) {
      get_URLS();
      setDoing('getURLs');
    }

    if (doing === 'getURLs' && urlList) {
      get_URLS();
      setDoing('');
    }

    if (doing === 'removeURLs' && urlRemovedResp?.status === 'success') {
      get_URLS();
      setDoing('getURLs');
    }
  }, [urlRemovedResp, urlAddedResp, doing]);

  // Gets the list of all urls logged against the user and their status and tags
  const get_URLS = () => {
    setLoading(true);
    getURLs(`/api/platform/knowledgebase_url/${combination_id}`, 'GET', null, false);
  };

  // Tell the backend to recrawl all urls
  // TODO
  // - needs actual endpoint
  const startCrawl = () => {
    crawlAllUrls(`/api/platform/trigger_crawler/`, 'POST', null, false);
    resetURLList();
    setDoing('crawlURLs');
  };

  // Pass the URL and any tags to the backend for saving
  const saveUrl = () => {
    setLoading(true);
    if (url.trim() === '') {
      setMessage('Please enter a URL');
      return;
    }
    setDoing('saveURLs');
    setUrl('');
    setTag('');

    // Make a request to the backend API
    const payload = {
      url: url,
      is_file: false,
      tags: tag,
    };
    addURLToKB(`/api/platform/knowledgebase_url/${combination_id}/`, 'POST', payload, false);
    resetURLList();
  };

  const removeURL = (e, item_id) => {
    setLoading(true);
    e.preventDefault();
    removeURLToKB(`/api/platform/knowledgebase_url/${item_id}/`, 'DELETE', null, false);
    setDoing('removeURLs');
    resetURLList();
  };

  const refresh_url_list = () => {
    // Check if an interval for the given job already exists
    if (jobStatusIntervalId) {
      return;
    }

    const intervalId = setInterval(() => {
      // check job loop
      get_URLS();
    }, 30000); // check every 30 seconds
    setJobStatusIntervalId(intervalId);
  };

  const stopPolling = () => {
    clearInterval(jobStatusIntervalId);
  };

  const save_this_config = () => {
    const payload = {
      variables: {
        arcos: {
          emails: {},
        },
      },
    };

    if (escalationContactEmail) {
      payload.variables.arcos.emails['ESCALATIONCONTACT'] = escalationContactEmail;
    }

    save_wf_config(payload);
  };

  return (
    <>
      {connectors.map((connector, key) => {
        return activeTab === 'wf-conn-' + key ? (
          <Col className="basic-form" key={'conn-info' + connector?.id}>
            <>
              {settingUpRecipe && connected ? (
                <>
                  <Preloader smallscreen={true} />
                  <br />
                  <h4 className="text-center">Getting your workflow ready</h4>
                </>
              ) : settingUpRecipe && !connected ? (
                <>
                  <br />
                  <h4 className="text-center">Let's Get Started</h4>
                  <br />
                  <p className="text-center">Please setup your connectors on the left.</p>
                </>
              ) : connector?.provider === 'arcanum_ai' ? (
                <>
                  <h3>Add to Arcos Knowledge Base</h3>
                  <br />
                  <label htmlFor="role" className="label-3col">
                    Add URL to scrape
                  </label>
                  <br />
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://"
                    className="form-control input-2col"
                  />
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    placeholder="Tag"
                    className="form-control input-1col"
                  />
                  <button
                    disabled={!url || currentlyCrawling}
                    onClick={saveUrl}
                    id="submit"
                    className="btn btn-primary x-5 float-end"
                  >
                    Save
                  </button>
                  <p>{message}</p>
                  <hr />
                  <label htmlFor="role" className="label-3col">
                    Arcos' Knowledge Base Library
                  </label>
                  <br />
                  {urlList && urlList.data.length > 0 ? (
                    <>
                      <Card className="p-4">
                        <ul className="list-unstyled">
                          {urlList.data.map((item) => (
                            <li key={item.id} className="d-flex align-items-center m-2">
                              <div className="flex-grow-1">
                                {item.url} -{' '}
                                {item.tags &&
                                  item.tags.split(',').map((tag) => (
                                    <span className="badge badge-status connected" key={tag}>
                                      {tag.trim()}
                                    </span>
                                  ))}
                                {item.status ? item.status : ''}
                              </div>
                              <button
                                onClick={(e) => removeURL(e, item.id)}
                                href="#"
                                rel="noopener"
                                id="submitRemove"
                                className="btn btn-secondary mx-2"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                          {currentlyCrawling && <div className="disabled_overlay"></div>}
                        </ul>

                        {crawlStatus === 'ERROR' && urlList['data'].length > 0 ? (
                          <Alert className="mt-3" key="danger" variant="danger">
                            We encountered a problem while crawling your URL(s).
                            <br />
                            Please check the urls are in the correct format and try again.
                          </Alert>
                        ) : crawlStatus === 'COMPLETED' && urlList['data'].length > 0 ? (
                          <Alert className="mt-3" key="success" variant="success">
                            Knowledgebase is ready.
                          </Alert>
                        ) : null}

                        {currentlyCrawling && (
                          <>
                            <hr />
                            <Preloader smallscreen={true} />
                            <p className="text-center">
                              Arcos is learning the updated knowledge
                              <br />
                              ETA 30 minutes
                            </p>
                          </>
                        )}
                      </Card>
                      <br />
                    </>
                  ) : loading ? (
                    <>
                      <Preloader smallscreen={true} />
                      <br />
                    </>
                  ) : null}
                  <button
                    disabled={currentlyCrawling}
                    onClick={startCrawl}
                    id="submit"
                    className="btn btn-primary x-5 float-end"
                  >
                    Learn the updated knowledge
                  </button>
                  <br /> <br />
                  <hr />
                  <strong>Select Arcos's key points of contact.</strong>
                  <br /> <br />
                  <p>
                    Arcos tries his best to answer customer-support queries. However, sometimes he won’t be able to, and
                    in these cases the query should be passed on to a human to answer:
                  </p>
                  <input
                    type="text"
                    value={escalationContactEmail}
                    onChange={(e) => setEscalationContactEmail(e.target.value)}
                    placeholder="escalationsupport@yourdomain.com"
                    className="form-control"
                  />
                  <hr />
                  <Button
                    id="submit"
                    disabled={!escalationContactEmail}
                    onClick={save_this_config}
                    className="btn btn-primary x-5 float-end"
                  >
                    Save Config
                  </Button>
                  <br />
                  <Alert className="mt-3" key={configAlertType} variant={configAlertType}>
                    {configAlertContent}
                  </Alert>
                </>
              ) : !recipeActive ? (
                <>
                  <br />
                  <h4 className="text-center">Workflow Stopped</h4>
                  <br />
                  {configRequired && !wf_configSet ? (
                    <p className="text-center">Please setup the configuration in the next step.</p>
                  ) : (
                    <p className="text-center">Please start your workflow to begin.</p>
                  )}
                </>
              ) : recipeActive ? (
                <>
                  <header>
                    <img
                      src={`/images/${connector?.icon}`}
                      className="icon-solutions float-left"
                      alt={connector?.name}
                    />
                  </header>
                  {connector?.provider === 'outlook' && connector?.name.toLowerCase().includes('input') ? (
                    /**
                     * Outlook or 365 input
                     */
                    <>
                      <h3>Outlook Input</h3>
                      <p>Emails received to this mailbox will be prosessed by Arcos.</p>
                      <br />
                    </>
                  ) : connector?.provider === 'outlook' && connector?.name.toLowerCase().includes('output') ? (
                    /**
                     * Outlook or 365 Outpu
                     */
                    <>
                      <h3>Outlook Output</h3>
                      <p>Emails will be sent via this mailbox by Arcos.</p>
                      <br />
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          </Col>
        ) : null;
      })}
    </>
  );
};

export { ArcosWorkflow };
